import { checkoutCopy } from './checkoutCopy';
import { checkoutLoginOptions } from './auth';
import { PaymentRedirectError, validatePaymentRedirect } from './paymentRedirect';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { clearIntent, intentFromSearch, parseIntent, readIntent, saveIntent } from './intent';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';

export default function CheckoutPage() {
  const location = useLocation();
  const [intent] = useState(
    () => parseIntent(location.state) ?? intentFromSearch(location.search) ?? readIntent(),
  );
  const {
    isLoading,
    isAuthenticated,
    user,
    loginWithRedirect,
    getAccessTokenSilently,
    error: authError,
  } = useAuth0();
  const [error, setError] = useState('');
  const hasActiveSubscription =
    error === 'Máte aktivní předplatné. Změnu tarifu proveďte v profilu.';
  const [busy, setBusy] = useState(false);
  const [choosingAccount, setChoosingAccount] = useState(false);
  const started = useRef(false);
  const copy = intent ? checkoutCopy(intent) : null;

  useEffect(() => {
    if (intent) saveIntent(intent);
  }, [intent]);
  const continuePayment = async () => {
    if (isLoading || !isAuthenticated || !intent || started.current) return;
    started.current = true;
    setBusy(true);
    setError('');
    try {
      const token = await getAccessTokenSilently();
      const { data } = await axios.post(
        `${import.meta.env.VITE_API_URL.replace(/\/$/, '')}/api/checkout/init`,
        intent,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const redirect = validatePaymentRedirect(data?.redirect);
      window.location.assign(redirect);
    } catch (e) {
      setError(
        e instanceof PaymentRedirectError
          ? e.message
          : axios.isAxiosError(e)
            ? e.response?.data?.message ||
              e.response?.data?.error ||
              'Platbu se nepodařilo připravit. Zkuste to znovu.'
            : 'Platbu se nepodařilo připravit. Zkuste to znovu.',
      );
      setBusy(false);
      started.current = false;
    }
  };

  const authenticate = async (method: 'google' | 'email' | 'login') => {
    if (!intent || busy) return;
    saveIntent(intent);
    setBusy(true);
    setError('');
    try {
      await loginWithRedirect(checkoutLoginOptions(intent, method, choosingAccount));
    } catch {
      setError('Přihlášení se nepodařilo spustit. Zkuste to znovu.');
      setBusy(false);
    }
  };
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Dialog open fullWidth maxWidth="xs" aria-labelledby="checkout-title">
        <DialogTitle id="checkout-title">
          {busy ? 'Chvilku strpení' : (copy?.title ?? 'Vyberte tarif')}
        </DialogTitle>
        <DialogContent>
          {!intent ? (
            <Alert severity="info">Nejprve vyberte tarif v ceníku.</Alert>
          ) : (
            <Stack spacing={2}>
              <Typography variant="body2">{copy?.description}</Typography>
              {(error || authError) && (
                <Alert severity="error">
                  {error || 'Přihlášení nebylo dokončeno. Zkuste to znovu.'}
                </Alert>
              )}
              {(isLoading || busy) && <CircularProgress aria-label="Načítání" />}
              {!isLoading && (!isAuthenticated || choosingAccount) && (
                <>
                  <Typography variant="body2">
                    Pro pokračování k platbě se zaregistrujte nebo přihlaste.
                  </Typography>
                  <Button
                    variant="outlined"
                    disabled={busy}
                    onClick={() => authenticate('google')}
                    startIcon={
                      <Box
                        component="span"
                        aria-hidden="true"
                        sx={{
                          width: 20,
                          height: 20,
                          backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' viewBox='0 0 48 48'%3E%3Cdefs%3E%3Cpath id='a' d='M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z'/%3E%3C/defs%3E%3CclipPath id='b'%3E%3Cuse xlink:href='%23a' overflow='visible'/%3E%3C/clipPath%3E%3Cpath clip-path='url(%23b)' fill='%23FBBC05' d='M0 37V11l17 13z'/%3E%3Cpath clip-path='url(%23b)' fill='%23EA4335' d='M0 11l17 13 7-6.1L48 14V0H0z'/%3E%3Cpath clip-path='url(%23b)' fill='%2334A853' d='M0 37l30-23 7.9 1L48 0v48H0z'/%3E%3Cpath clip-path='url(%23b)' fill='%234285F4' d='M48 48L17 24l-4-3 35-10z'/%3E%3C/svg%3E")`,
                          backgroundSize: 'contain',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'center',
                        }}
                      />
                    }
                    sx={{
                      backgroundColor: '#fff',
                      color: '#3c4043',
                      borderColor: '#dadce0',
                      '&:hover': {
                        backgroundColor: '#fff',
                        borderColor: '#0e0e0f',
                        color: '#0e0e0f',
                      },
                      '&.Mui-disabled': { backgroundColor: '#fff' },
                    }}
                  >
                    Registrovat přes Google
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={busy}
                    onClick={() => authenticate('email')}
                    startIcon={<AlternateEmailIcon />}
                  >
                    Registrovat e-mailem a heslem
                  </Button>
                  <Button disabled={busy} onClick={() => authenticate('login')}>
                    Už mám účet – přihlásit se
                  </Button>
                </>
              )}
              {!isLoading && isAuthenticated && !choosingAccount && (
                <>
                  <Typography sx={{ overflowWrap: 'anywhere' }}>
                    Přihlášeni jako {user?.email || user?.name || 'stávající uživatel'}.
                  </Typography>
                  {hasActiveSubscription ? (
                    <Button
                      variant="contained"
                      disabled={busy}
                      href="/hairdresser/profile"
                      onClick={clearIntent}
                    >
                      Přihlásit
                    </Button>
                  ) : (
                    <>
                      <Typography variant="body2">Pokračujte k zabezpečené platbě.</Typography>
                      <Button variant="contained" disabled={busy} onClick={continuePayment}>
                        {error ? 'Zkusit platbu znovu' : 'Pokračovat k platbě'}
                      </Button>
                    </>
                  )}
                  <Button
                    disabled={busy}
                    onClick={() => {
                      setError('');
                      setChoosingAccount(true);
                    }}
                  >
                    Začít znovu s jiným účtem
                  </Button>
                </>
              )}
              {choosingAccount && (
                <>
                  <Alert severity="info">
                    Vyberte přihlášení nebo registraci s jiným e-mailem. Původní rozpracovaná platba
                    zůstane u původního účtu.
                  </Alert>
                  <Button disabled={busy} onClick={() => setChoosingAccount(false)}>
                    Zpět k původnímu účtu
                  </Button>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button href={import.meta.env.VITE_APP_URL + '/#cenik'} onClick={clearIntent}>
            Zpět do ceníku
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
